<template>
  <div class="base-autocomplete" :class="{ 'is-open': showDropdown && filteredItems.length > 0 }">
    <label v-if="label" class="base-autocomplete__label">{{ label }}</label>
    <div class="base-autocomplete__input-wrapper">
      <input
        ref="inputRef"
        type="text"
        class="base-autocomplete__input"
        :value="searchText"
        :placeholder="placeholder"
        :disabled="disabled"
        autocomplete="off"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="onEnter"
        @keydown.esc="onEscape"
      />
      <button
        v-if="clearable && searchText"
        type="button"
        class="base-autocomplete__clear"
        @mousedown.prevent="clear"
      >
        <i class="fa fa-times"></i>
      </button>
      <span v-if="loading" class="base-autocomplete__loading">
        <i class="fa fa-spinner fa-spin"></i>
      </span>
    </div>
    <div
      v-show="showDropdown && filteredItems.length > 0"
      class="base-autocomplete__dropdown"
    >
      <div
        v-for="(item, index) in filteredItems"
        :key="getItemValue(item)"
        class="base-autocomplete__item"
        :class="{ 'is-highlighted': index === highlightedIndex }"
        @mousedown.prevent="selectItem(item)"
      >
        {{ getItemTitle(item) }}
      </div>
    </div>
    <div v-if="showDropdown && !loading && filteredItems.length === 0 && searchText && !hideNoData" class="base-autocomplete__dropdown">
      <div class="base-autocomplete__no-data">검색 결과가 없습니다</div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'BaseAutocomplete',
  props: {
    modelValue: {
      type: [String, Number, Object],
      default: null
    },
    items: {
      type: Array,
      default: () => []
    },
    itemTitle: {
      type: String,
      default: 'title'
    },
    itemValue: {
      type: String,
      default: 'value'
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    hideNoData: {
      type: Boolean,
      default: false
    },
    search: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'update:search', 'blur'],
  data() {
    return {
      searchText: '',
      showDropdown: false,
      highlightedIndex: -1,
      internalSearch: ''
    }
  },
  computed: {
    filteredItems() {
      return this.items || []
    }
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (val !== null && val !== undefined) {
          // Find the item that matches the value
          const item = this.items.find(i => this.getItemValue(i) === val)
          if (item) {
            this.searchText = this.getItemTitle(item)
          }
        } else {
          this.searchText = ''
        }
      }
    },
    items: {
      handler(newItems) {
        // When items change, try to update display text
        if (this.modelValue !== null && this.modelValue !== undefined) {
          const item = newItems.find(i => this.getItemValue(i) === this.modelValue)
          if (item) {
            this.searchText = this.getItemTitle(item)
          }
        }
      }
    },
    search: {
      immediate: true,
      handler(val) {
        if (val !== this.internalSearch) {
          this.searchText = val
          this.internalSearch = val
        }
      }
    }
  },
  methods: {
    getItemTitle(item) {
      if (typeof item === 'string') return item
      return item[this.itemTitle] || item.title || item.name || ''
    },
    getItemValue(item) {
      if (typeof item === 'string') return item
      return item[this.itemValue] || item.value || item.id || item
    },
    onInput(e) {
      const val = e.target.value
      this.searchText = val
      this.internalSearch = val
      this.showDropdown = true
      this.highlightedIndex = -1
      this.$emit('update:search', val)
    },
    onFocus() {
      this.showDropdown = true
    },
    onBlur() {
      // Delay to allow click events on dropdown items
      setTimeout(() => {
        this.showDropdown = false
        this.$emit('blur')
      }, 200)
    },
    onArrowDown() {
      if (this.highlightedIndex < this.filteredItems.length - 1) {
        this.highlightedIndex++
      }
    },
    onArrowUp() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
      }
    },
    onEnter() {
      if (this.highlightedIndex >= 0 && this.highlightedIndex < this.filteredItems.length) {
        this.selectItem(this.filteredItems[this.highlightedIndex])
      }
    },
    onEscape() {
      this.showDropdown = false
    },
    selectItem(item) {
      const value = this.getItemValue(item)
      this.searchText = this.getItemTitle(item)
      this.$emit('update:modelValue', value)
      this.showDropdown = false
      this.highlightedIndex = -1
    },
    clear() {
      this.searchText = ''
      this.internalSearch = ''
      this.$emit('update:modelValue', null)
      this.$emit('update:search', '')
      this.$refs.inputRef?.focus()
    }
  }
}
</script>

<style scoped>
.base-autocomplete {
  position: relative;
  width: 100%;
}

.base-autocomplete__label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.base-autocomplete__input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.base-autocomplete__input {
  width: 100%;
  padding: 8px 12px;
  padding-right: 32px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.base-autocomplete__input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.base-autocomplete__input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.base-autocomplete__clear {
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.base-autocomplete__clear:hover {
  color: #6b7280;
}

.base-autocomplete__loading {
  position: absolute;
  right: 8px;
  color: #9ca3af;
}

.base-autocomplete__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
}

.base-autocomplete__item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.15s;
}

.base-autocomplete__item:hover,
.base-autocomplete__item.is-highlighted {
  background-color: #f3f4f6;
}

.base-autocomplete__no-data {
  padding: 8px 12px;
  font-size: 14px;
  color: #9ca3af;
  text-align: center;
}
</style>
